import {
	ColDef,
	Column,
	GridApi,
	IRowNode,
	RowSelectionOptions,
	SelectionChangedEvent,
} from 'ag-grid-community';
import { CustomCellRendererProps, CustomHeaderProps } from 'ag-grid-react';
import { MouseEvent, ReactNode, useEffect, useReducer } from 'react';
import styled from 'styled-components';

import { Checkbox } from '../form';
import { Tooltip } from './Tooltip';

const DataGridCheckboxClickArea = styled.label<{ $disabled?: boolean }>`
	height: 40px;
	width: 40px;
	display: flex;
	justify-content: center;
	align-items: center;
	margin: -8px;
	cursor: ${(props) => (props.$disabled ? 'default' : 'pointer')};
`;

function withDisabledTooltip(checkbox: ReactNode, disabled: boolean, disabledTooltip?: string) {
	if (disabled && disabledTooltip) {
		return (
			<Tooltip content={disabledTooltip} portal position="auto">
				{checkbox}
			</Tooltip>
		);
	}

	return checkbox;
}

function DataGridSelectionCheckbox(props: {
	checked: boolean;
	disabled?: boolean;
	disabledTooltip?: string;
	onToggle: (event: MouseEvent) => void;
}) {
	const { checked, disabled, disabledTooltip, onToggle } = props;

	return withDisabledTooltip(
		<DataGridCheckboxClickArea $disabled={disabled} onClick={onToggle}>
			<Checkbox checked={checked} disabled={disabled} mr={0} />
		</DataGridCheckboxClickArea>,
		disabled ?? false,
		disabledTooltip,
	);
}

export interface DataGridSelectionCellParams<TData = unknown> {
	isRowDisabled?: (data: TData) => boolean;
	disabledTooltip?: string;
}

function DataGridSelectionCell<TData>({
	node,
	data,
	isRowDisabled,
	disabledTooltip,
}: CustomCellRendererProps<TData> & DataGridSelectionCellParams<TData>) {
	if (!data) {
		return null;
	}

	const disabled = isRowDisabled ? isRowDisabled(data) : node.selectable === false;
	const checked = node.isSelected() ?? false;

	const toggle = (event: MouseEvent) => {
		event.stopPropagation();
		event.preventDefault();

		if (!disabled) {
			node.setSelected(!checked);
		}
	};

	return (
		<DataGridSelectionCheckbox
			checked={checked}
			disabled={disabled}
			disabledTooltip={disabledTooltip}
			onToggle={toggle}
		/>
	);
}

export interface DataGridSelectionHeaderParams<TData = unknown> {
	isNodeSelectable?: (node: IRowNode<TData>) => boolean;
	disabledTooltip?: string;
}

export function getDataGridSelectableNodes<TData>(
	api: GridApi<TData>,
	isNodeSelectable?: (node: IRowNode<TData>) => boolean,
): IRowNode<TData>[] {
	const nodes: IRowNode<TData>[] = [];

	api.forEachNode((node) => {
		if (isNodeSelectable ? isNodeSelectable(node) : node.selectable !== false) {
			nodes.push(node);
		}
	});

	return nodes;
}

function DataGridSelectionHeader<TData>({
	api,
	isNodeSelectable,
	disabledTooltip,
}: CustomHeaderProps<TData> & DataGridSelectionHeaderParams<TData>) {
	const [, refresh] = useReducer((value) => value + 1, 0);

	useEffect(() => {
		const onSelectionChanged = () => refresh();

		api.addEventListener('selectionChanged', onSelectionChanged);

		return () => api.removeEventListener('selectionChanged', onSelectionChanged);
	}, [api]);

	const selectableNodes = getDataGridSelectableNodes(api, isNodeSelectable);
	const allSelected =
		selectableNodes.length > 0 && selectableNodes.every((node) => node.isSelected());
	const disabled = selectableNodes.length === 0;

	const toggle = (event: MouseEvent) => {
		event.stopPropagation();
		event.preventDefault();

		if (disabled) {
			return;
		}

		api.setNodesSelected({ nodes: selectableNodes, newValue: !allSelected });
	};

	return (
		<DataGridSelectionCheckbox
			checked={allSelected}
			disabled={disabled}
			disabledTooltip={disabledTooltip}
			onToggle={toggle}
		/>
	);
}

export interface CreateDataGridSelectionColumnDefOptions<TData = unknown> {
	isRowDisabled?: (data: TData) => boolean;
	isNodeSelectable?: (node: IRowNode<TData>) => boolean;
	disabledTooltip?: string;
}

export function createDataGridSelectionColumnDef<TData = unknown>(
	options: CreateDataGridSelectionColumnDefOptions<TData> = {},
): ColDef<TData> {
	return {
		colId: 'selection',
		headerName: '',
		width: 52,
		minWidth: 52,
		maxWidth: 52,
		flex: 0,
		sortable: false,
		resizable: false,
		suppressMovable: true,
		lockPosition: 'left',
		pinned: 'left',
		headerComponent: DataGridSelectionHeader,
		headerComponentParams: {
			isNodeSelectable: options.isNodeSelectable,
			disabledTooltip: options.disabledTooltip,
		},
		cellRenderer: DataGridSelectionCell,
		cellRendererParams: {
			isRowDisabled: options.isRowDisabled,
			disabledTooltip: options.disabledTooltip,
		},
		cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
	};
}

export function createDataGridMultiRowSelection<TData = unknown>(options?: {
	isRowSelectable?: (params: { data: TData | undefined }) => boolean;
}): RowSelectionOptions<TData> {
	return {
		mode: 'multiRow',
		checkboxes: false,
		headerCheckbox: false,
		enableClickSelection: false,
		isRowSelectable: options?.isRowSelectable,
	};
}

export function isDataGridSelectionColumn(column: Column | null | undefined) {
	return column?.getColId() === 'selection';
}

export function refreshDataGridSelectionCells<TData>(api: GridApi<TData>) {
	api.refreshCells({ columns: ['selection'], force: true });
}

export function handleDataGridSelectionChanged<TData>(
	event: SelectionChangedEvent<TData>,
	onSelectionChange?: (rows: TData[]) => void,
) {
	onSelectionChange?.(event.api.getSelectedRows());
	refreshDataGridSelectionCells(event.api);
}
